import StorageService from "./abstracts/StorageService";
import { iPendingForumComment } from "@/interfaces/forum";

interface iPendingForumComments {
  [key: string]: iPendingForumComment;
}

type iEntityType = "comment" | "thread";

class PendingForumComments extends StorageService<iPendingForumComments> {
  /**
   * Empty comment object.
   *
   * @var {iPendingForumComment}
   */
  public static get pendingComment(): iPendingForumComment {
    const comment = {
      body: "",
      entities: [],
      uploads: [],
      upload_files: {
        images: [],
        audio: [],
        documents: [],
        videos: [],
      },
    };
    Object.freeze(comment);
    return comment;
  }

  /**
   * @var {string}
   */
  public readonly storeName = "pending_forum_comments";

  /**
   * Gets the value of a pending comment.
   *
   * @param {iEntityType} parentType
   * @param {number} parentId
   * @returns {Promise<iPendingForumComment>}
   */
  public async getComment(
    parentType: iEntityType,
    parentId: number,
  ): Promise<iPendingForumComment> {
    const comments = await this.get();
    return Promise.resolve(
      comments[this.getKey(parentType, parentId)] ||
        PendingForumComments.pendingComment,
    );
  }

  /**
   * Saves a pending comment.
   *
   * @param {iEntityType} parentType
   * @param {number} parentId
   * @param {iPendingForumComment} comment
   * @returns {Promise<void>}
   */
  public async setComment(
    parentType: iEntityType,
    parentId: number,
    comment: iPendingForumComment,
  ): Promise<void> {
    const comments = await this.get();
    comments[this.getKey(parentType, parentId)] = comment;
    this.set(comments);
  }

  /**
   * Deletes a pending comment.
   *
   * @param {iEntityType} parentType
   * @param {number} parentId
   * @returns {Promise<void>}
   */
  public async deleteComment(
    parentType: iEntityType,
    parentId: number,
  ): Promise<void> {
    const comments = await this.get();
    delete comments[this.getKey(parentType, parentId)];
    this.set(comments);
  }

  /**
   * Gets the key that a comment will be saved under in the stored object.
   *
   * @param {iEntityType} parentType
   * @param {number} parentId
   * @returns {string}
   */
  protected getKey(parentType: iEntityType, parentId: number): string {
    return `${parentType}.${parentId}`;
  }

  /**
   * The default value of this store.
   *
   * @returns {iPendingForumComments}
   */
  protected get defaultStoreValue(): iPendingForumComments {
    return {};
  }
}

export default new PendingForumComments();
